/*
 * bc-rig-figure — "this is the rig those specimens were pulled in", as an
 * interactive 3D figure inside one group of a batch record.
 *
 * A LINE DRAWING THAT CAN BE PICKED UP. The figure IS the still image the
 * server rendered: a line-art view of the test setup with the batch's own
 * webbing baked into it, an ordinary <img> in light DOM that costs one small
 * file, prints, and is the whole figure with JS off. Pressing "View in 3D"
 * swaps it for a live canvas showing the same assembly from the same pose, and
 * from there the reader can turn it over — which is the one question a
 * photograph of a rig never answers: what is behind it.
 *
 * NOTHING IS FETCHED UNTIL THAT PRESS. A batch page can carry a rig figure in
 * every strength group, and three.js plus a packed GLB is most of a megabyte
 * each; the record is a document first, so the models are opt-in, one press at
 * a time. There is deliberately no IntersectionObserver BOOT here — scrolling
 * past a figure must cost nothing at all. (There is one visibility observer,
 * started only once a viewer exists, and all it does is stop the render loop —
 * and with it any idle auto-rotate — from running off screen; see
 * #watchVisibility and syncRenderLoop.)
 *
 * THE MODELS ARE BAKED AND ARE NEVER RECOLORED. The webbing's real design is
 * painted into the GLB's materials by the publishing pipeline (one model per
 * batch × configuration), so this file has no picker, no palette and no
 * variant listeners: a metaobject page has no variant context to read anyway.
 * That is the whole difference from assets/bc-installed-diagram.js, which this
 * viewer is lifted from — same pinned three.js, same meshopt wiring, same
 * free-tumble trackball, same arm-to-interact rule, same expand dialog, and
 * kept in step with it by eye.
 *
 * Everything is data. The element reads one JSON config child, emitted by
 * snippets/bc-batch-rig-figure.liquid from the batch's `test_models` entry for
 * this group:
 *
 *   {"src":   "<GLB url>",           // required; nothing renders without it
 *    "still": "<svg/png url>",       // the server's business — see below
 *    "alt":   "…",                   // the MODEL's description
 *    "view":  {"direction": [x, y, z], "up": [x, y, z],
 *              "distance": 1.15, "fov": 35},   // optional; see DEFAULT_*
 *    "autoRotate": false}
 *
 * `still` is carried so the config is a faithful copy of the record's entry
 * (one thing to read when a figure looks wrong), and is NOT used here: the
 * still is server markup, because an image that only appears once JS has run
 * is not a fallback for JS. Its own alt text (`stillAlt`, describing a
 * drawing rather than an interactive model) is the server's business too, and
 * is not carried here at all.
 *
 * GESTURES, the same contract every bc 3D surface uses: inert until ARMED, so
 * a canvas in the middle of a long record can never eat a page scroll or a
 * wheel. The press that loads the model arms it (the reader asked to handle
 * the thing, so they are not made to press twice). Armed = free tumble (drag),
 * pan (right-drag / shift-drag / two-finger drag), wheel and pinch zoom, plus
 * Expand into a full-window dialog. Tapping off it releases it.
 *
 * AND THE DRAWING COMES BACK. The swap is not one-way: the × in the armed
 * controls row and Escape both put the still back over the canvas (#toStill),
 * stop the render loop and return the badge, so a reader who picked the rig up
 * to look behind it can set it down again. Nothing is torn down — the context
 * and the parsed GLB stay warm, and the next press is a fade (#toModel) with
 * no second megabyte. `data-bc-still` is the flag on the element; `data-bc-
 * state=ready` is what actually cross-fades the two pictures, and it now goes
 * on and off rather than being set once.
 */
(() => {
  if (customElements.get('bc-rig-figure')) return;

  /** Pinned three.js — same version, CDN and `/+esm` reasoning as bc-model-3d.js. */
  const THREE_BASE = 'https://cdn.jsdelivr.net/npm/three@0.165.0';
  const MODULE_URLS = [
    `${THREE_BASE}/+esm`,
    `${THREE_BASE}/examples/jsm/loaders/GLTFLoader.js/+esm`,
    `${THREE_BASE}/examples/jsm/environments/RoomEnvironment.js/+esm`,
  ];

  /**
   * The opening pose, as a direction from the part toward the camera plus a
   * screen-up vector in the MODEL's own (Z-up CAD) frame — steptool.py's VIEWS
   * convention, which transfers verbatim into the camera basis built below.
   *
   * THE DEFAULT IS THE STILL'S OWN VIEW, deliberately: `(0, 0, 1)` up
   * `(0, 1, 0)` is `top` in that table, and the still each figure opens on is
   * the un-suffixed `<stem>.svg` the assembly scripts write, which IS their top
   * view (the suffixed `-front` / `-iso` files are the other two). So pressing
   * the badge turns the drawing into the same drawing, alive — not into a
   * camera somewhere else. An entry that ever renders its still from another
   * view has to state the matching `view` beside it.
   *
   * DEFAULT_DISTANCE frames from the bounding box: the model's bounding-box
   * DIAGONAL times this factor. 1.25 is set for what these figures actually
   * are — long, low assemblies in a 16:9 frame, seen from above — and opens at
   * roughly the drawing's own size rather than dropping the model into the
   * middle of a field of air the moment the still goes. It is snug enough that
   * turning the long axis vertical can crop it, which is what the wheel and the
   * pinch are for; a rig that needs otherwise says so in its own
   * `view.distance`.
   */
  const DEFAULT_DIRECTION = [0, 0, 1];
  const DEFAULT_UP = [0, 1, 0];
  const DEFAULT_DISTANCE = 1.25;
  const DEFAULT_FOV = 35;

  /**
   * The meshopt decoder — what makes a `gltfpack -cc` GLB readable, pinned to
   * the EXACT version that packs them (gltfpack is meshoptimizer's own CLI, one
   * source tree, one version number; the file is `meshopt_decoder.mjs` since
   * 1.0, `meshopt_decoder.module.js` in 0.x).
   *
   * No `/+esm`: that suffix exists to rewrite the three.js addons' bare `three`
   * import, and this module imports nothing, so the pinned file is already the
   * ES module we want.
   *
   * It FAILS SOFT (a CDN hiccup, or no WebAssembly, resolves to null) because a
   * rejection would take the whole figure down over a decoder an uncompressed
   * GLB never needed; a compressed GLB with no decoder still fails properly, at
   * GLTFLoader.parse, which #load already handles.
   *
   * Same wiring and same reasoning as assets/bc-installed-diagram.js and
   * assets/bc-model-3d.js, deliberately — the three viewers are kept in step by
   * eye.
   */
  const MESHOPT_URL = 'https://cdn.jsdelivr.net/npm/meshoptimizer@1.2.0/meshopt_decoder.mjs';

  const loadMeshopt = () =>
    import(MESHOPT_URL).then(
      async (mod) => {
        const decoder = mod?.MeshoptDecoder;
        if (!decoder?.supported) return null;
        await decoder.ready;
        return decoder;
      },
      () => null
    );

  /** One module graph for every figure on the page, however many are pressed. */
  let libPromise = null;
  const loadLib = () =>
    (libPromise ||= Promise.all([...MODULE_URLS.map((url) => import(url)), loadMeshopt()]));

  const asVec = (value, fallback) =>
    Array.isArray(value) && value.length === 3 && value.every((n) => Number.isFinite(Number(n)))
      ? value.map(Number)
      : fallback;

  class BcRigFigure extends HTMLElement {
    /** @type {any} parsed config child */ #config = null;
    /** @type {any} three.js module bag */ #three = null;
    /** @type {any} */ #viewer = null;
    /** @type {Promise<ArrayBuffer>|null} GLB bytes, held only until parsed */ #bytes = null;
    /** @type {Promise<any>|null} the one load; a second press awaits it */ #boot = null;
    /** @type {IntersectionObserver|null} visibility only — it never loads anything */ #observer = null;
    #armed = false;
    #expanded = false;
    #failed = false;
    /**
     * Does the reader still WANT this armed? Set by the press that starts a
     * load, cleared the moment they release it. A GLB can take seconds, and
     * arming on arrival alone would hand the wheel to a figure the reader has
     * long since scrolled past.
     */
    #wantArm = false;
    /**
     * Is any part of this figure near the viewport? Read by the render loop —
     * both by the idle auto-rotate and by whether the loop runs at all. The
     * model still loads, draws and arms exactly as it did off screen. True
     * until an observer says otherwise, so nothing depends on the first
     * callback having run.
     */
    #onScreen = true;
    /**
     * Has the reader asked for the DRAWING back? Set by the × and by Escape
     * (see #toStill): the still fades in over the canvas, the render loop
     * stops, and the "View in 3D" badge returns — the figure reads exactly as
     * it did before the first press, except that everything behind it is still
     * loaded, so the next press is a fade rather than another megabyte.
     */
    #stillOnly = false;

    connectedCallback() {
      // A re-connect (or a node move): everything is already built, and the
      // canvas is still a child of this element, so its GL context and its
      // parsed GLB come along with it. Only the visibility observer has to be
      // re-established — and only if there is a live viewer for it to quiet —
      // and the loop, which the disconnect stopped, restarted.
      if (this.#config) {
        if (this.#viewer && !this.#observer) this.#watchVisibility();
        this.syncRenderLoop();
        return;
      }

      try {
        this.#config = JSON.parse(this.querySelector('script[data-bc-rig-figure]')?.textContent || '');
      } catch (_e) {
        return; // malformed config — the still image stands on its own
      }
      if (!this.#config?.src) {
        this.#config = null;
        return;
      }

      // Tells the stylesheet a viewer is possible here: it is what shows the
      // "View in 3D" control. Without JS, with a malformed config, or before
      // this runs, the figure is the still image and no dead affordance —
      // which is also what #fail() drops back to.
      this.dataset.bcJs = '';
      this.addEventListener('click', this.#onClick);
    }

    disconnectedCallback() {
      this.#observer?.disconnect();
      this.#observer = null;
      // Nothing on screen can be this element's any more, so the loop stops
      // here: the observer above is the only thing that would have stopped it,
      // and it has just been disconnected. Everything else stays — the canvas
      // is a child of this element, so a re-connect resumes with its GL context
      // and its parsed GLB, and syncRenderLoop starts the loop again.
      this.syncRenderLoop();
    }

    /**
     * Run the render loop only while there is something to render TO: the
     * figure near the viewport (or its overlay open), the tab in front, and
     * the model — rather than the drawing — the picture the reader chose.
     *
     * `renderer.setAnimationLoop(null)` here is only ever the loop stopping,
     * never the viewer being torn down — everything it owns survives, which is
     * what makes starting again one call and one frame. Public because the
     * document-level `visibilitychange` listener at the foot of this file calls
     * it on every figure.
     */
    syncRenderLoop() {
      const wanted =
        !this.#failed &&
        !this.#stillOnly &&
        this.isConnected &&
        (this.#onScreen || this.#expanded) &&
        document.visibilityState !== 'hidden';
      this.#viewer?.setLoopRunning(wanted);
    }

    /**
     * THE ONE OBSERVER IN THIS FILE, and it loads nothing — it flips a flag the
     * render loop reads. Started when a viewer is built, so a figure nobody
     * pressed never has one at all, which keeps "nothing happens until the tap"
     * true of the observer as well as of the bytes.
     *
     * Without it a loaded figure keeps a 60 fps callback — and, with
     * auto-rotate on, a spinning re-render — running for the rest of the
     * session, several sections above wherever the reader currently is.
     */
    #watchVisibility() {
      if (this.#observer || typeof IntersectionObserver !== 'function') return;
      this.#observer = new IntersectionObserver(
        (entries) => {
          const on = entries.some((entry) => entry.isIntersecting);
          if (on === this.#onScreen) return;
          this.#onScreen = on;
          // Coming back into view: the loop has been stopped, so flag a frame
          // for it to draw as it starts (an auto-rotate has been skipping its
          // rotation, and a repaint may have been asked for while it was off).
          if (on) this.#viewer?.invalidate();
          this.syncRenderLoop();
        },
        { rootMargin: '200px 0px' }
      );
      this.#observer.observe(this);
    }

    /* ---------------------------------------------------------------- *
     * Loading
     * ---------------------------------------------------------------- */

    /**
     * THE ENTRY POINT: the reader has asked for the model. Idempotent — a
     * second press awaits the same promise and simply re-arms.
     *
     * The GLB's BYTES and the three.js module graph are independent network
     * work (a packed assembly off the Shopify CDN, an ESM graph off jsDelivr)
     * and the parse needs both, so they start in the same tick rather than the
     * download queueing behind the import.
     */
    #load() {
      if (this.#failed) return Promise.resolve(null);
      if (this.#boot) {
        // Loaded, or still loading: this press is the GATE. Arming rides the
        // same promise rather than happening now, so an impatient second press
        // neither refetches anything nor takes the badge — and its "Loading
        // 3D…" — away before there is a model to hand over.
        return this.#boot.then((viewer) => {
          if (viewer) this.#armFromPress();
          return viewer;
        });
      }
      this.dataset.bcState = 'loading';
      this.#fetchGlb();
      this.#boot = this.#buildViewer()
        .then((viewer) => (viewer ? this.#showModel().then(() => viewer) : null))
        .then((viewer) => {
          if (!viewer) {
            this.#fail();
            return null;
          }
          this.#armFromPress();
          return viewer;
        })
        .catch(() => {
          this.#fail();
          return null;
        });
      return this.#boot;
    }

    /**
     * Arm because a press asked for it — the reader asked to handle the model,
     * and making them press a second time to be allowed to would read as the
     * control not having worked.
     *
     * ONLY IF THEY STILL WANT IT. A load can take seconds; `#wantArm` is
     * cleared by Escape, by a tap anywhere off the figure and by the disarm ×,
     * so a reader who pressed and moved on does not have a figure arm itself
     * (and start eating the wheel) somewhere above them when the GLB lands.
     *
     * And move focus onto Expand, because the badge that was just pressed is
     * `display: none` the instant it arms — keyboard focus would otherwise fall
     * to <body>, leaving the reader at the top of the document. Expand is the
     * next control in the figure's own reading order, and its sibling × is one
     * Tab away.
     */
    #armFromPress() {
      if (!this.#wantArm || this.#armed) return;
      // A press on a figure the × sent back to its drawing is a request for the
      // model again, and then for the handle on it — one press, both things,
      // exactly as the press that loaded it in the first place behaved.
      this.#toModel();
      this.#setArmed(true);
      if (!this.#armed) return; // #fail() got there first
      /** @type {HTMLElement|null} */ (this.querySelector('[data-bc-rig-expand]'))?.focus({
        preventScroll: true,
      });
    }

    /**
     * No WebGL, or a GLB that would not load: back to the still image, with
     * the control taken away rather than left to fail again. Dropping
     * `data-bc-js` is the whole of it — the stylesheet hangs the badge, the
     * canvas and the controls off that flag.
     */
    #fail() {
      this.#failed = true;
      delete this.dataset.bcJs;
      this.dataset.bcState = 'error';
      this.#setArmed(false);
      // The canvas is `display: none` from here on (the stylesheet hangs the
      // badge, the canvas and the controls off `data-bc-js`), so the loop has
      // nothing left to draw to. This is the one true teardown in the file:
      // setAnimationLoop(null), and it is never started again.
      this.#viewer?.setLoopRunning(false);
      // Nothing will parse them now — and a machine with no WebGL is exactly
      // the one that should not hold a GLB's worth of ArrayBuffer for the rest
      // of the session.
      this.#bytes = null;
    }

    /**
     * The model's BYTES, kept apart from parsing them because the two wait on
     * different things: the download needs only the network, the parse needs
     * three.js to have finished importing.
     */
    #fetchGlb() {
      if (this.#bytes) return this.#bytes;
      const url = this.#config.src;
      const bytes = fetch(url, { credentials: 'omit' }).then((response) => {
        // GLTFLoader's own FileLoader treats a 404 as an error; fetch treats it
        // as a response, so the status check is what keeps the two equivalent
        // (an error page parsed as a GLB would fail anyway, just later and with
        // a stranger message).
        if (!response.ok) throw new Error(`HTTP ${response.status} for ${url}`);
        return response.arrayBuffer();
      });
      bytes.catch(() => {}); // handled by #load; this only silences the console
      this.#bytes = bytes;
      return bytes;
    }

    /* ---------------------------------------------------------------- *
     * The viewer
     * ---------------------------------------------------------------- */

    async #buildViewer() {
      const [THREE, gltfMod, roomMod, meshopt] = await loadLib();
      this.#three = { THREE, gltfMod, roomMod, meshopt };

      const stage = this.querySelector('[data-bc-rig-stage]');
      if (!stage) return null;

      let renderer;
      try {
        renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      } catch (_e) {
        return null; // no WebGL — the still image stays
      }
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      // Neutral (Khronos PBR), not ACES filmic: ACES darkens and desaturates
      // saturated albedos, which read the baked webbing designs' vivid store
      // hexes back as muted pastels. Neutral keeps the colour near the authored
      // hex — the whole point of baking the batch's own webbing into the model.
      renderer.toneMapping = THREE.NeutralToneMapping;
      renderer.domElement.setAttribute('aria-hidden', 'true');
      stage.appendChild(renderer.domElement);

      const scene = new THREE.Scene();
      try {
        // The generator and the room are SCAFFOLDING — a set of intermediate
        // render targets, a pile of blur materials and a boxed-up room, all of
        // it spent the instant the cubemap is baked. Only the texture outlives
        // this block, and it has to: it is what the metal reflects.
        const pmrem = new THREE.PMREMGenerator(renderer);
        const room = new roomMod.RoomEnvironment();
        scene.environment = pmrem.fromScene(room, 0.04).texture;
        pmrem.dispose();
        this.#disposeTree(room);
      } catch (_e) {
        /* the explicit lights below carry the scene on their own */
      }
      scene.add(new THREE.HemisphereLight(0xffffff, 0x444455, 1.2));
      const key = new THREE.DirectionalLight(0xffffff, 1.6);
      key.position.set(60, 80, 120);
      scene.add(key);
      const fill = new THREE.DirectionalLight(0xffffff, 0.6);
      fill.position.set(-80, -40, -100);
      scene.add(fill);

      const camera = new THREE.PerspectiveCamera(DEFAULT_FOV, 1, 0.01, 2000);
      const pivot = new THREE.Group();
      scene.add(pivot);

      let dist = 1;
      let minDist = 0.1;
      let maxDist = 100;
      let panLimit = Infinity;
      let homeDist = 1;
      let dirty = true;
      camera.position.set(0, 0, dist);

      /**
       * A frame is WANTED. Every "something moved" in this viewer goes through
       * here rather than assigning `dirty` directly, because the loop is not
       * always running (see setLoopRunning): the flag on its own would be a
       * repaint that never happens.
       */
      const mark = () => {
        dirty = true;
        this.syncRenderLoop();
      };

      /** Is the rAF callback installed? See setLoopRunning. */
      let looping = false;

      const viewer = {
        stage,
        renderer,
        scene,
        camera,
        pivot,
        autoRotate:
          Boolean(this.#config.autoRotate) && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
        invalidate: mark,
        /**
         * Install or drop the rAF callback.
         *
         * Render-on-demand already keeps the GPU idle, but the CALLBACK is not
         * free: it runs 60 times a second for the life of the page, and a
         * record can carry one of these per test configuration. Stopping it is
         * `setAnimationLoop(null)` — the renderer, the context, the environment
         * and the parsed model stay exactly as they are, so starting again is
         * one call and one frame, with nothing to rebuild.
         *
         * The clock is read and thrown away on the way in: `getDelta` measures
         * from the last read, so an idle stretch would otherwise arrive at the
         * auto-rotate as one enormous delta and snap the model round.
         */
        setLoopRunning(on) {
          if (on === looping) return;
          looping = on;
          if (!on) {
            renderer.setAnimationLoop(null);
            return;
          }
          clock.getDelta();
          renderer.setAnimationLoop(frame);
        },
        /** Distance, clip planes and limits from the model's own size — these
         *  GLBs are in metres and a test rig is a metre or two across. */
        frame(size, distanceFactor, fov) {
          camera.fov = fov;
          dist = size * distanceFactor;
          homeDist = dist;
          minDist = size * 0.4;
          maxDist = size * 12;
          // 1.5 bounding RADII off centre, so a pan can never lose the model.
          panLimit = size * 0.75;
          camera.near = size / 100;
          camera.far = size * 100;
          camera.position.set(0, 0, dist);
          pivot.position.set(0, 0, 0);
          camera.updateProjectionMatrix();
          mark();
        },
        homeFraming() {
          dist = homeDist;
          camera.position.set(0, 0, dist);
          pivot.position.set(0, 0, 0);
          mark();
        },
        /**
         * The opening pose, stated by #showModel once the model is on stage —
         * the view the STILL was drawn from (see DEFAULT_DIRECTION).
         */
        homeQuaternion: null,
        /**
         * Framing AND orientation. Used by the one path that has to forget the
         * turn the reader gave the model: putting the drawing back (#toStill).
         * The still is one particular view of the rig, so coming out of it
         * facing some other way would read as the press having moved the
         * model. Everywhere else the face they turned toward themselves is
         * theirs to keep — see #collapse, which homes the framing alone.
         */
        homePose() {
          if (this.homeQuaternion) pivot.quaternion.copy(this.homeQuaternion);
          this.homeFraming();
        },
        resize() {
          const width = stage.clientWidth;
          const height = stage.clientHeight;
          if (!width || !height) return;
          renderer.setSize(width, height);
          camera.aspect = width / height;
          camera.updateProjectionMatrix();
          // Redraw here rather than flagging the loop: setSize reallocates (and
          // so clears) the drawing buffer, and the ResizeObserver runs after the
          // frame's rAF callbacks — a deferred redraw would paint one empty frame.
          dirty = false;
          renderer.render(scene, camera);
        },
        zoomBy(factor) {
          dist = Math.min(maxDist, Math.max(minDist, dist * factor));
          camera.position.setLength(dist);
          mark();
        },
        /** Pan in CANVAS PIXELS: the model slides in the camera's screen plane
         *  and the pointer keeps hold of the point it grabbed. Moving the pivot
         *  (never the camera) keeps screen right/up equal to world +X/+Y. */
        panBy(dxPixels, dyPixels) {
          const perPixel =
            (2 * dist * Math.tan((camera.fov * Math.PI) / 360)) / (renderer.domElement.clientHeight || 1);
          pivot.position.set(pivot.position.x + dxPixels * perPixel, pivot.position.y - dyPixels * perPixel, 0);
          const offset = Math.hypot(pivot.position.x, pivot.position.y);
          if (offset > panLimit) pivot.position.multiplyScalar(panLimit / offset);
          mark();
        },
        /** Free tumble: the model turns about the CAMERA's screen axes, so there
         *  is no pole to stick at and no upright it snaps back to. */
        tumble(dx, dy) {
          const q = new THREE.Quaternion()
            .setFromAxisAngle(new THREE.Vector3(0, 1, 0), dx)
            .multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), dy));
          pivot.quaternion.premultiply(q);
          mark();
        },
      };

      new ResizeObserver(() => viewer.resize()).observe(stage);

      const clock = new THREE.Clock();
      const frame = () => {
        const delta = clock.getDelta();
        // Render-on-demand: the loop runs, the GPU only works when something
        // moved — and the one thing that moves on its own is skipped entirely
        // while the figure is off screen, so an idle spin cannot burn a phone's
        // battery several sections above the reader. (Belt and braces with
        // setLoopRunning, which stops the loop outright in that case; this is
        // what covers the frames between going off screen and the observer
        // saying so.)
        if (viewer.autoRotate && this.#onScreen && !this.#armed && !this.#expanded) {
          pivot.rotateOnWorldAxis(new THREE.Vector3(0, 1, 0), delta * 0.35);
          // Straight to the flag: the loop is running by definition in here, so
          // going through mark() would ask to start it once a frame forever.
          dirty = true;
        }
        if (!dirty) return;
        dirty = false;
        renderer.render(scene, camera);
      };
      viewer.setLoopRunning(true);

      this.#viewer = viewer;
      this.#wireGestures(viewer);
      this.#watchVisibility();
      // Only now can the element's own rule be applied — setLoopRunning above
      // started the loop against a null #viewer, which is what draws the first
      // frame; this stops it again if the figure is somehow already off screen
      // or the tab has gone to the background mid-load.
      this.syncRenderLoop();
      return viewer;
    }

    /**
     * Parse the GLB and put it on stage at its opening pose. One model, once —
     * there is nothing to swap to (a group's rig is its rig), which is why this
     * file carries no model cache and none of bc-installed-diagram's eviction
     * rules.
     *
     * WHAT THAT TRADES AWAY, stated plainly: the page holds one ELEMENT per
     * test configuration, and each one the reader presses keeps its own WebGL
     * context and its own parsed model for the rest of the session. Today a
     * record runs to at most about six figures, of which a reader presses one
     * or two, so the ceiling is well inside what a mid-range phone answers for.
     * If a record ever grows past that — or if presses start being cheap enough
     * that a reader opens them all — the answer is a shared stage re-parented
     * between figures (assets/bc-model-3d.js does exactly that across gallery
     * hosts), not an LRU per element.
     */
    async #showModel() {
      const { THREE, gltfMod, meshopt } = this.#three;
      const src = this.#config.src;
      const buffer = await this.#fetchGlb();

      const gltf = await new Promise((resolve, reject) => {
        // parse(), not load(): the bytes are already here. The second argument
        // is the base URL any external resource inside the file resolves
        // against — these GLBs are self-contained, but a sibling .bin or
        // texture still resolves exactly as load() made it, since this is
        // three's own extractUrlBase spelled out.
        const base = src.slice(0, src.lastIndexOf('/') + 1) || './';
        const loader = new gltfMod.GLTFLoader();
        // Reached only by a GLB that declares EXT_meshopt_compression, so an
        // uncompressed model parses exactly as it would have (see MESHOPT_URL).
        if (meshopt) loader.setMeshoptDecoder(meshopt);
        loader.parse(buffer, base, resolve, reject);
      });
      // Parsed: the bytes have done their job and a multi-MB ArrayBuffer held
      // in a field is a leak with a long name.
      this.#bytes = null;

      const model = gltf.scene;
      const box = new THREE.Box3().setFromObject(model);
      model.position.sub(box.getCenter(new THREE.Vector3()));
      const size = box.getSize(new THREE.Vector3()).length() || 1;

      // bc: the webbing's printed cross-section rides as skins standing 0.02 mm
      // (sewn-loop rig) / 0.004 mm (wrap rigs) off the solid body named
      // `webbing`. Zoomed out, that is under the depth buffer's resolution and
      // the body fights through the skins — purple streaks across a green
      // bottom face on a two-tone webbing. Push the BODY back two depth units
      // (plus one slope unit) so the skins always win; the skins and threads are
      // left alone, so nothing else in the model moves.
      model.traverse((node) => {
        const material = /** @type {any} */ (node).material;
        for (const entry of Array.isArray(material) ? material : [material]) {
          if (entry?.name !== 'webbing') continue;
          entry.polygonOffset = true;
          entry.polygonOffsetFactor = 1;
          entry.polygonOffsetUnits = 2;
          entry.needsUpdate = true;
        }
      });

      // render_glb.py names a view by the direction from the part toward the
      // camera plus a screen-up vector, both in the model's own frame. Its
      // camera_axes turns the pair into the model-space vectors that map to
      // screen right / up / toward-viewer; the rotation we want takes those
      // onto the world axes the camera sits on — that basis, transposed.
      const view = this.#config.view || {};
      const zDir = new THREE.Vector3(...asVec(view.direction, DEFAULT_DIRECTION)).normalize();
      const xDir = new THREE.Vector3(...asVec(view.up, DEFAULT_UP)).cross(zDir).normalize();
      const yDir = new THREE.Vector3().crossVectors(zDir, xDir);
      const home = new THREE.Quaternion().setFromRotationMatrix(
        new THREE.Matrix4().makeBasis(xDir, yDir, zDir).transpose()
      );

      this.#viewer.pivot.add(model);
      this.#viewer.pivot.quaternion.copy(home);
      // Kept, so the × can put the model back the way the drawing has it.
      this.#viewer.homeQuaternion = home;
      this.#viewer.frame(size, Number(view.distance) || DEFAULT_DISTANCE, Number(view.fov) || DEFAULT_FOV);
      this.#viewer.resize();
      // Held back until the model is framed and drawn, so the still image is
      // never traded for an empty box: `ready` is what fades the one out and
      // the other in. It is a STATE from here on rather than a milestone —
      // #toStill takes it off again and #toModel puts it back.
      this.dataset.bcState = 'ready';
    }

    /**
     * Give the environment scaffolding's GPU memory back. three uploads
     * geometry buffers and textures lazily and holds them until it is TOLD to
     * let go: dropping the last JS reference frees the JS objects and none of
     * the VRAM.
     *
     * Geometries and materials are shared BETWEEN meshes, so the walk carries a
     * `seen` set. Texture slots are ordinary material properties (map,
     * normalMap, roughnessMap, …) that vary by material type, so they are found
     * by shape rather than from a hardcoded list.
     *
     * Deliberately NOT disposed: the scene environment. It is the renderer's,
     * baked once at build time, and the model reflects it — so it is skipped by
     * identity even if a material points at it.
     */
    #disposeTree(root) {
      const environment = this.#viewer?.scene.environment || null;
      const seen = new Set();
      root.removeFromParent?.();
      root.traverse((node) => {
        const geometry = /** @type {any} */ (node).geometry;
        if (geometry?.dispose && !seen.has(geometry)) {
          seen.add(geometry);
          geometry.dispose();
        }
        const material = /** @type {any} */ (node).material;
        if (!material) return;
        for (const one of Array.isArray(material) ? material : [material]) {
          if (!one || seen.has(one)) continue;
          seen.add(one);
          for (const value of Object.values(one)) {
            if (value?.isTexture && value !== environment && !seen.has(value)) {
              seen.add(value);
              value.dispose();
            }
          }
          one.dispose?.();
        }
      });
    }

    /* ---------------------------------------------------------------- *
     * Arming, expanding, gestures
     * ---------------------------------------------------------------- */

    #interactive() {
      return this.#armed || this.#expanded;
    }

    #syncTouchAction() {
      const canvas = this.#viewer?.renderer.domElement;
      if (canvas) canvas.style.touchAction = this.#interactive() ? 'none' : '';
    }

    #setArmed(on) {
      if (this.#armed === on) return;
      if (on && this.#failed) return;
      this.#armed = on;
      if (on) this.dataset.bcArmed = '';
      else delete this.dataset.bcArmed;
      this.#syncTouchAction();
    }

    /**
     * BACK TO THE DRAWING — the × and Escape, and nothing else.
     *
     * The figure a reader came for is the line elevation; the model is what
     * they picked it up to answer a question with, and before this there was
     * no way to put it down again. So the `ready` flag that faded the still
     * out comes back off (the stylesheet cross-fades the two), the loop stops,
     * and the pose goes home so the next press opens on the drawing's own view
     * rather than wherever the model was left.
     *
     * NOTHING IS TORN DOWN. The renderer, the context, the environment and the
     * parsed GLB all stay — `#boot` is resolved, so the next press takes
     * #load's already-loaded branch and #toModel below fades the canvas back in
     * with no network at all. (The one true teardown in this file is still
     * #fail, and it is still never undone.)
     *
     * A tap OFF the figure does not come here: that only disarms, because
     * releasing the wheel is one thing and taking the picture away from
     * someone who is reading beside it is another.
     */
    #toStill() {
      if (!this.#viewer || this.#stillOnly || this.#expanded) return;
      this.#wantArm = false;
      this.#setArmed(false);
      this.#stillOnly = true;
      this.dataset.bcStill = '';
      // The state the still's `opacity: 0` rule hangs off. Deleted rather than
      // set to some third value: 'ready' and 'loading' are the only two the
      // stylesheet knows, and the figure is back to neither.
      delete this.dataset.bcState;
      // Set the flag FIRST, so the repaint this asks for is owed rather than
      // drawn: syncRenderLoop now answers "no loop", and #toModel pays it.
      this.#viewer.homePose();
      this.syncRenderLoop();
      /** @type {HTMLElement|null} */ (this.querySelector('[data-bc-rig-arm]'))?.focus({
        preventScroll: true,
      });
    }

    /**
     * …and back to the model. Called by the press that re-arms a figure the ×
     * put away — the badge is the same control it always was, and from here it
     * costs one frame.
     */
    #toModel() {
      if (!this.#stillOnly) return;
      this.#stillOnly = false;
      delete this.dataset.bcStill;
      this.dataset.bcState = 'ready';
      // Loop first (so the viewer has one to draw in), then the frame it owes:
      // the last setSize cleared the drawing buffer, and the box may have
      // reflowed while the canvas was hidden.
      this.syncRenderLoop();
      this.#viewer?.resize();
    }

    #onClick = (event) => {
      const target = /** @type {Element|null} */ (event.target);
      if (!target?.closest) return;

      if (target.closest('[data-bc-rig-arm]')) {
        event.preventDefault();
        // LOADER FIRST, GATE AFTER. On an unloaded figure this press starts
        // three.js, the GLB and the WebGL context and arms the result; once
        // loaded it is the gesture gate the badge always was. The intent is
        // recorded rather than acted on, because the arming may be seconds away
        // (see #armFromPress).
        this.#wantArm = true;
        this.#load();
        return;
      }
      if (target.closest('[data-bc-rig-expand]')) {
        event.preventDefault();
        this.#expand();
        return;
      }
      // The × in the armed controls row: not "stop interacting" but "put the
      // drawing back", which is the whole of what it was missing. Disarming is
      // part of that rather than the end of it — see #toStill.
      if (target.closest('[data-bc-rig-disarm]')) {
        event.preventDefault();
        this.#toStill();
      }
    };

    /**
     * Full-window overlay — a <dialog> opened with showModal(), NOT the
     * Fullscreen API (iOS Safari does not support it for non-video elements).
     * The stage is re-parented in and back out, so the GL context and the
     * parsed GLB survive the trip.
     */
    #expand() {
      const dialog = /** @type {HTMLDialogElement|null} */ (this.querySelector('dialog[data-bc-rig-dialog]'));
      const target = dialog?.querySelector('[data-bc-rig-dialog-stage]');
      const stage = this.querySelector('[data-bc-rig-stage]');
      if (!dialog || !target || !stage) return;

      this.#expanded = true;
      this.dataset.bcExpanded = '';
      target.appendChild(stage);
      dialog.showModal();
      this.#lockScroll(true);
      this.#syncTouchAction();
      // The overlay outranks the observer: the figure's own box may be scrolled
      // off behind a full-window dialog, and the loop has to run for what is on
      // top of it.
      this.syncRenderLoop();
      requestAnimationFrame(() => this.#viewer?.resize());
      /** @type {HTMLElement|null} */ (dialog.querySelector('[data-bc-rig-close]'))?.focus({
        preventScroll: true,
      });

      if (!dialog.dataset.bcWired) {
        dialog.dataset.bcWired = '1';
        dialog.querySelector('[data-bc-rig-close]')?.addEventListener('click', () => dialog.close());
        dialog.addEventListener('close', () => this.#collapse());
        // …and the `open` attribute as well, because the `close` event is the
        // ONE signal here that cannot be relied on: it is absent in at least
        // one Chrome build (verified while building bc-installed-diagram), and
        // losing it would strand the canvas inside a closed dialog with the
        // page still scroll-locked. The attribute is definitive — Escape,
        // close(), the close button and a form[method=dialog] submit all clear
        // it — and #collapse is a no-op when it is not expanded, so the two
        // signals can both fire.
        new MutationObserver(() => {
          if (!dialog.open) this.#collapse();
        }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
      }
    }

    #collapse() {
      if (!this.#expanded) return;
      this.#expanded = false;
      delete this.dataset.bcExpanded;
      this.#lockScroll(false);
      // Framing (distance and pan), not orientation: a crop taken in a
      // full-window overlay is wrong in a figure a third of the size, but the
      // face they turned toward themselves is theirs to keep.
      this.#viewer?.homeFraming();
      const stage = this.querySelector('[data-bc-rig-stage]');
      const home = this.querySelector('[data-bc-rig-stage-home]');
      if (stage && home) home.appendChild(stage);
      this.#syncTouchAction();
      // Back in the flow, so the observer's answer governs again — which may be
      // "off screen" if the page moved under the overlay.
      this.syncRenderLoop();
      requestAnimationFrame(() => this.#viewer?.resize());
      /** @type {HTMLElement|null} */ (this.querySelector('[data-bc-rig-expand]'))?.focus({
        preventScroll: true,
      });
    }

    /**
     * Horizon locks root scrolling with an attribute on <html> (base.css
     * `html[scroll-lock]`). Its owner bookkeeping lives in a module a classic
     * script cannot import, so mirror the attribute — and never clear it while
     * another dialog still wants it.
     */
    #lockScroll(on) {
      if (on) {
        document.documentElement.setAttribute('scroll-lock', '');
        return;
      }
      // Any OTHER open modal keeps the lock (ours has already lost its `open`
      // by the time collapse runs, so a match here is always someone else's).
      if (document.querySelector('dialog[open]')) return;
      document.documentElement.removeAttribute('scroll-lock');
    }

    #wireGestures(viewer) {
      const canvas = viewer.renderer.domElement;
      /** @type {Map<number, {x: number, y: number}>} */
      const pointers = new Map();
      let pinchDistance = 0;
      let midX = 0;
      let midY = 0;

      const spread = () => {
        const [a, b] = [...pointers.values()];
        return Math.hypot(a.x - b.x, a.y - b.y);
      };
      const midpoint = () => {
        const [a, b] = [...pointers.values()];
        return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      };

      /**
       * A one-pointer drag pans while the RIGHT button is down or Shift is held
       * — two ways in, because a right-drag on a trackpad is a contortion. It
       * LATCHES for the rest of the drag: a shift-drag actually ends by letting
       * go of Shift a moment before the button, which would otherwise spin the
       * model on the last few pixels of every pan.
       */
      let panDrag = false;
      const panning = (event) => {
        panDrag ||= Boolean(event.buttons & 2) || event.shiftKey;
        return panDrag;
      };

      canvas.addEventListener('pointerdown', (event) => {
        if (!this.#interactive()) return;
        canvas.setPointerCapture(event.pointerId);
        if (pointers.size === 0) panDrag = false;
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        panning(event);
        if (pointers.size === 2) {
          pinchDistance = spread();
          ({ x: midX, y: midY } = midpoint());
        }
      });

      canvas.addEventListener('pointermove', (event) => {
        if (!this.#interactive() || !pointers.has(event.pointerId)) return;
        const previous = pointers.get(event.pointerId);
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        // Two fingers are ONE gesture read two ways off the same pair: what the
        // spread does is zoom, what the midpoint does is pan.
        if (pointers.size >= 2) {
          const next = spread();
          const mid = midpoint();
          if (pinchDistance > 0 && next > 0) viewer.zoomBy(pinchDistance / next);
          pinchDistance = next;
          viewer.panBy(mid.x - midX, mid.y - midY);
          midX = mid.x;
          midY = mid.y;
          return;
        }

        if (panning(event)) {
          viewer.panBy(event.clientX - previous.x, event.clientY - previous.y);
          return;
        }

        // Radians-ish per canvas height, so the feel is size-independent.
        const scale = 3.2 / (canvas.clientHeight || 1);
        viewer.tumble((event.clientX - previous.x) * scale, (event.clientY - previous.y) * scale);
      });

      const release = (event) => {
        pointers.delete(event.pointerId);
        if (pointers.size < 2) pinchDistance = 0;
        if (pointers.size === 0) panDrag = false;
      };
      canvas.addEventListener('pointerup', release);
      canvas.addEventListener('pointercancel', release);

      // A right-drag is a pan, so the menu would land at the end of every one.
      // An un-armed canvas is a picture, and a picture keeps the browser's menu.
      canvas.addEventListener('contextmenu', (event) => {
        if (!this.#interactive()) return;
        event.preventDefault();
      });

      // Exponential, so the perceived step is the same 50 mm or 500 mm out, and
      // it can never cross zero. Not passive: an armed model owns the wheel.
      canvas.addEventListener(
        'wheel',
        (event) => {
          if (!this.#interactive()) return;
          event.preventDefault();
          viewer.zoomBy(Math.exp(event.deltaY * 0.0007));
        },
        { passive: false }
      );
    }

    /**
     * Called by the document-level listeners below. It also withdraws the
     * PENDING intent, which is the whole of the "pressed it, then scrolled on"
     * case: the load carries on (the bytes are already in flight and the model
     * is worth having if they come back), it simply does not seize the wheel
     * when it lands.
     */
    releaseIfArmed() {
      if (this.#expanded) return;
      this.#wantArm = false;
      if (!this.#armed) return;
      this.#setArmed(false);
      this.#viewer?.homeFraming();
    }

    /**
     * ESCAPE, the keyboard's ×: all the way back to the drawing, not merely a
     * release. Same precondition as the button it stands in for — the figure
     * armed and inline — so an Escape meant for something else on the page
     * cannot fold up a figure the reader never pressed, and an Escape out of
     * the expand overlay is left to the dialog (this runs BEFORE the UA
     * processes the close request, so `#expanded` is still set and this is
     * still the right answer to give).
     */
    dismissIfArmed() {
      if (this.#expanded) return;
      this.#wantArm = false;
      if (!this.#armed) return;
      this.#toStill();
    }
  }

  customElements.define('bc-rig-figure', BcRigFigure);

  const each = (fn) => {
    for (const el of document.querySelectorAll('bc-rig-figure')) fn(/** @type {any} */ (el));
  };

  // Tapping anywhere off a figure releases it, so the page gets its scroll back
  // without the reader having to find the × first.
  //
  // EVERY FIGURE BUT THE ONE CLICKED IN. A record carries one of these per test
  // configuration, and a click inside figure B is a click OFF figure A — an
  // earlier "is this click inside any bc-rig-figure?" test left A armed for the
  // rest of the session, so two or three armed canvases sat in the document
  // each holding the wheel over their own box. Only the host is spared.
  document.addEventListener('click', (event) => {
    const target = /** @type {Element|null} */ (event.target);
    const host = target?.closest?.('bc-rig-figure') || null;
    each((el) => {
      if (el !== host) el.releaseIfArmed();
    });
  });
  // Escape goes FURTHER than a tap off the figure: it is the × without having
  // to find it, so it puts the drawing back rather than only handing the wheel
  // over. (A tap off is a click somewhere else on the page and means nothing
  // about the figure; Escape is aimed at whatever has been picked up.)
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') each((el) => el.dismissIfArmed());
  });

  // A backgrounded tab gets no rAF callbacks anyway in every current browser,
  // but "anyway" is the browser's promise, not ours: a loaded figure left in a
  // background tab should hold nothing but memory, and this is what says so.
  // One listener for every figure rather than one per element, so nothing has
  // to be unhooked when a node moves.
  document.addEventListener('visibilitychange', () => each((el) => el.syncRenderLoop()));
})();
